// src/pages/AboutWayroot.jsx
import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Icon from "@mdi/react";
import { mdiCarOutline, mdiSteering } from "@mdi/js";

const BRAND = {
  pink: "#FF2D83",
  black: "#0B0E13",
  fog: "#F7F7FB",
};

const GRADIENT =
  "linear-gradient(135deg,#0B0E13 0%,#8A0F3A 35%,#C11867 70%,#FF2D83 100%)";

/* Reusable buttons (solid + outline) for consistent styling */
function BtnPrimary({ to, children }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center rounded-2xl px-6 py-3.5 text-base font-semibold
                 text-white shadow-sm transition-transform active:scale-[0.98]"
      style={{ background: BRAND.black }}
    >
      {children}
    </Link>
  );
}

function BtnSecondary({ to, children }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center rounded-2xl border border-slate-300 px-6 py-3.5
                 text-base font-semibold text-slate-800 bg-white hover:bg-white
                 transition-transform active:scale-[0.98]"
    >
      {children}
    </Link>
  );
}

export default function AboutWayroot() {
  return (
    <main className="min-h-screen bg-white text-slate-900">
      <Header />
      <Hero />
      <SectionDivider />
      <ValueGrid />
      <SectionDivider />
      <UseCases />
      <SectionDivider />
      <HowItWorksResponsive />
      <SectionDivider />
      <Platform />
      <SectionDivider />
      <FAQ />
      <FinalCTA />
      <Footer />
    </main>
  );
}

/* =========================
   Hero (About Wayroot)
   ========================= */
function Hero() {
  return (
    <section
      className="relative overflow-hidden"
      style={{
        background:
          `radial-gradient(1200px 600px at 85% -20%, ${BRAND.pink}22, transparent 60%),` +
          `radial-gradient(900px 450px at 10% -10%, ${BRAND.black}0F, transparent 60%),` +
          `linear-gradient(180deg, white, ${BRAND.fog})`,
      }}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-16 pb-8 md:pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              About Wayroot
            </p>
            <h1 className="mt-2 text-5xl sm:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Reserve <span className="text-[#FF2D83]">one</span> or many cars —
              <br />
              with one seamless checkout
            </h1>

            <p className="mt-5 text-lg sm:text-xl text-slate-600 max-w-2xl">
              Wayroot is a reservations platform designed for events, corporates,
              and group travel. True reservations (hours or days), live
              availability, mixed fleets, multi-stop routes, and round trips —
              all in a single plan.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <BtnPrimary to="/discover">Start a booking</BtnPrimary>
              <BtnSecondary to="/owners/onboard">List a car</BtnSecondary>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3 text-sm">
              <Badge icon={<ShieldIcon />}>Verified owners</Badge>
              <Badge icon={<LockIcon />}>Secure payments</Badge>
              <Badge icon={<BoltIcon />}>Instant availability</Badge>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* =========================
   Section Divider
   ========================= */
function SectionDivider() {
  return (
    <div className="relative">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="h-px w-full bg-slate-100" />
      </div>
    </div>
  );
}

/* =========================
   Value Grid (Benefits)
   ========================= */
function ValueGrid() {
  const items = [
    {
      title: "Multi-car in one flow",
      desc: "Reserve 1, 10, or any number of cars together — mix Hatchback, Sedan, SUV, or luxury as needed.",
    },
    {
      title: "True reservations",
      desc: "Block vehicles for hours or days — ideal for ceremonies, offsites, and VIP logistics.",
    },
    {
      title: "Live availability",
      desc: "Only see cars that match your dates, trip type and are available for booking.",
    },
    {
      title: "Stops & round trips",
      desc: "Add multiple stops, return to origin, or build custom routes within a single booking.",
    },
    {
      title: "One plan, one checkout",
      desc: "Mix vehicle types and quantities in a single cart and pay once—no juggling multiple bookings.",
    },
    {
      title: "Transparent pricing",
      desc: "Clear pricing with proper breakup up front before booking.",
    },
  ];

  return (
    <section className="py-16 bg-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <h2 className="text-3xl font-bold">
            Plan once, reserve many, manage online
          </h2>
          <p className="mt-2 text-slate-600">
            Eliminate phone calls and spreadsheets. Wayroot gives teams clarity,
            speed, and control.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((it) => (
            <div
              key={it.title}
              className="group relative rounded-2xl border border-slate-200 bg-white p-6 shadow-sm hover:shadow-md transition"
            >
              <div
                className="absolute inset-x-0 -top-px h-[3px] rounded-t-2xl"
                style={{ background: GRADIENT }}
              />
              <div className="flex items-start gap-4">
                <GradientIcon />
                <div>
                  <h3 className="text-[1.05rem] font-semibold">{it.title}</h3>
                  <p className="mt-1 text-sm text-slate-600 leading-6">
                    {it.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =========================
   Use Cases
   ========================= */
function UseCases() {
  const cases = [
    {
      title: "Events & ceremonies",
      tagline: "End-to-end planning for gatherings of any scale.",
      bullets: [
        "Mix & match any quantity: hatchback, sedan, SUV, or luxury in a single checkout",
        "See live availability instantly—no calling around or guessing which cars are free",
        "Upfront, transparent pricing with clear inclusions before you confirm",
      ],
    },
    {
      title: "Travel & city logistics",
      tagline: "City tours, intercity runs, and airport movements.",
      bullets: [
        "Plan airport arrivals with buffers and grouped pick-ups",
        "Build custom tours with multiple stops and language prefs",
        "Support onward journeys or return-to-origin trips",
      ],
    },
    {
      title: "Single-vehicle booking",
      tagline: "Point-to-point, round trip, or full-day reservations.",
      bullets: [
        "Pick a vehicle type, set pickup & drop (add optional stops) with instant confirmation",
        "Reserve by hours or an entire day with accurate ETAs and schedule buffers",
        "Transparent pricing with clear inclusions and fare breakup",
      ],
    },
  ];

  return (
    <section className="relative py-24 bg-white">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            `radial-gradient(900px 300px at 10% -10%, ${BRAND.pink}0F, transparent 60%),` +
            `radial-gradient(900px 300px at 90% -15%, ${BRAND.pink}0A, transparent 60%)`,
        }}
      />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <header className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-xs text-slate-600">
            <span
              className="h-1.5 w-1.5 rounded-full"
              style={{ background: BRAND.pink }}
            />
            Use cases
          </div>
          <h2 className="mt-2 text-3xl font-bold text-slate-900">
            Built for real-world logistics
          </h2>
          <p className="mt-2 text-slate-600">
            Create one plan, reserve the exact mix of vehicles, and keep
            everyone informed.
          </p>
        </header>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {cases.map((c) => (
            <UseCaseCard key={c.title} {...c} />
          ))}
        </div>
      </div>
    </section>
  );
}

function UseCaseCard({ title, tagline, bullets }) {
  return (
    <article className="relative rounded-3xl border border-slate-200 bg-white p-6 shadow-sm hover:shadow-md transition overflow-hidden">
      <div
        className="absolute inset-x-0 -top-px h-[2px] rounded-t-3xl"
        style={{ background: GRADIENT }}
        aria-hidden="true"
      />

      <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
      <p className="mt-1 text-sm leading-6 text-slate-600">{tagline}</p>

      <ul className="mt-5 space-y-3 text-sm">
        {bullets.map((b) => (
          <ChecklistItem key={b}>{b}</ChecklistItem>
        ))}
      </ul>
    </article>
  );
}

function ChecklistItem({ children }) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 grid h-5 w-5 place-items-center rounded-full bg-white ring-1 ring-slate-200 shadow-sm">
        <svg
          viewBox="0 0 24 24"
          className="h-3.5 w-3.5"
          fill="none"
          stroke={BRAND.pink}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6 12l4 4 8-8" />
        </svg>
      </span>
      <span className="leading-6 text-slate-700">{children}</span>
    </li>
  );
}

/* =========================
   How it works (desktop + mobile)
   ========================= */

function useIsDesktop(breakpoint = 768) {
  const get = () =>
    typeof window !== "undefined"
      ? window.innerWidth >= breakpoint
      : true;

  const [isDesktop, setIsDesktop] = React.useState(get);

  React.useEffect(() => {
    const onResize = () => setIsDesktop(get());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isDesktop;
}

function HowItWorksResponsive() {
  const isDesktop = useIsDesktop(768);
  return isDesktop ? <HowItWorks /> : <HowItWorksMobile />;
}

function HowItWorks() {
  const steps = [
    { title: "Category", desc: "Start with Normal or Luxury as your booking category." },
    { title: "Car type", desc: "Select type of car Hatchback, Sedan, or SUV as per your need." },
    { title: "Trip details", desc: "Add pickup/drop, stops, dates & times, and a search radius." },
    { title: "Filter & add to cart", desc: "Filter by color, price, brand/model, age, fuel type—add to cart." },
    { title: "Single checkout", desc: "Book everything together with one transparent, secure payment." },
  ];

  return (
    <section className="py-16" style={{ background: BRAND.fog }}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <h2 className="text-3xl font-bold">How booking works</h2>
          <p className="mt-2 text-slate-600">
            Clean steps, clear context, single checkout.
          </p>
        </div>

        <div className="relative mt-8">
          <div
            className="pointer-events-none absolute inset-x-0 top-12 hidden h-[3px] rounded-full md:block -z-10"
            style={{
              background:
                "linear-gradient(90deg, rgba(2,6,23,0.06), rgba(255,45,131,0.55), rgba(2,6,23,0.06))",
            }}
          />
          <div className="mt-0 flex gap-6 overflow-x-auto snap-x snap-mandatory pb-2 md:grid md:grid-cols-5 md:overflow-visible md:pb-0">
            {steps.map((s, i) => (
              <StepCardNumber key={s.title} index={i + 1} {...s} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function StepCardNumber({ index, title, desc }) {
  return (
    <article
      className="relative z-10 snap-start flex-none w-[300px] md:w-auto rounded-2xl border border-slate-200 bg-white p-6
                 shadow-sm hover:shadow-md transition"
    >
      <div className="flex items-center gap-2">
        <NumberBadge value={index} />
        <h3 className="text-base font-semibold leading-6">{title}</h3>
      </div>
      <p className="mt-3 text-sm leading-6 text-slate-600">{desc}</p>
    </article>
  );
}

function NumberBadge({ value }) {
  return (
    <span
      aria-label={`Step ${value}`}
      className="grid place-items-center h-6 w-6 md:h-7 md:w-7 rounded-full shrink-0
                 text-white text-[11px] md:text-[12px] font-semibold
                 ring-2 ring-white/90 shadow-[0_1px_6px_rgba(255,45,131,0.14)]"
      style={{ backgroundColor: BRAND.pink }}
    >
      {value}
    </span>
  );
}

function HowItWorksMobile() {
  const steps = [
    { title: "Category", desc: "Start with Normal or Luxury as your booking category." },
    { title: "Car type", desc: "Select type of car Hatchback, Sedan, or SUV as per your need." },
    { title: "Trip details", desc: "Add pickup/drop, stops, dates & times, and a search radius." },
    { title: "Filter & add to cart", desc: "Filter by color, price, brand/model, age, fuel type—add to cart." },
    { title: "Single checkout", desc: "Book everything together with one transparent, secure payment." },
  ];

  return (
    <section className="py-16 md:hidden" style={{ background: BRAND.fog }}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="max-w-3xl">
          <h2 className="text-3xl font-bold">How booking works</h2>
          <p className="mt-2 text-slate-600">
            Clean steps, clear context, single checkout.
          </p>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4">
          {steps.map((s, i) => (
            <article
              key={s.title}
              className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
            >
              <div className="flex items-center gap-2">
                <NumberBadge value={i + 1} />
                <h3 className="text-base font-semibold leading-6">{s.title}</h3>
              </div>
              <p className="mt-3 text-sm leading-6 text-slate-600">{s.desc}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =========================
   Platform (owners & drivers)
   ========================= */
function Platform() {
  const ownerBullets = [
    "List unlimited cars with basic details & docs",
    "Auto-match bookings near you (≈10 km by default)",
    "Full availability calendar; freeze any dates",
    "Choose service zones per car (city/state/multi-state)",
    "See upcoming & past bookings with live status",
    "Assign only your rostered/linked drivers",
    "Earnings dashboard with forecasts & downloadable reports",
  ];

  const driverBullets = [
    "Dashboard shows assigned jobs to accept or decline",
    "All trip info in one place: pickup, stops, route, timings",
    "Clear list of upcoming, accepted, and past rides",
    "Predictable earnings with booking forecasts",
    "Work only with owners you’re associated with",
  ];

  return (
    <section className="py-16 bg-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <h2 className="text-3xl font-bold">Built for owners &amp; drivers</h2>
          <p className="mt-2 text-slate-600">
            List, assign, track, and earn with full transparency—and a clear
            forecast of bookings and income.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <BtnPrimary to="/owners/onboard">List a car</BtnPrimary>
            <BtnSecondary to="/drivers/apply">Partner as a driver</BtnSecondary>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Panel
            title="For owners"
            subtitle="List cars, control bookings, manage drivers, and get paid."
            bullets={ownerBullets}
            iconPath={mdiCarOutline}
          />
          <Panel
            title="For drivers"
            subtitle="Accept jobs, see routes clearly, and maximise earnings."
            bullets={driverBullets}
            iconPath={mdiSteering}
          />
        </div>
      </div>
    </section>
  );
}

function Panel({ title, subtitle, bullets, iconPath }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm hover:shadow-md transition">
      <div className="flex items-center gap-3">
        <span className="h-5 w-5 text-slate-800 inline-flex items-center justify-center">
          <Icon path={iconPath} size={0.9} color="currentColor" />
        </span>
        <h3 className="text-lg font-semibold">{title}</h3>
      </div>
      {subtitle && (
        <p className="mt-1 text-sm text-slate-600">{subtitle}</p>
      )}

      <ul className="mt-5 space-y-2.5 text-sm text-slate-700">
        {bullets.map((b) => (
          <li key={b} className="flex gap-2 items-start">
            <span
              className="mt-2 h-1.5 w-1.5 rounded-full flex-none"
              style={{ background: BRAND.pink }}
            />
            <span className="leading-6">{b}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* =========================
   FAQ
   ========================= */
function FAQ() {
  const items = [
    {
      q: "How are prices calculated?",
      a: "Prices depend on distance, trip duration, fuel, tolls, and the selected vehicle. You’ll see a clear total before payment.",
    },
    {
      q: "Can I book multiple vehicle types together?",
      a: "Yes. Book any number of cars—Normal or Luxury—across Hatchback, Sedan, and SUV in one plan.",
    },
    {
      q: "Do owners control availability?",
      a: "Yes. Owners manage their calendar. All days are open by default, and owners can freeze specific dates to make a car unavailable.",
    },
    {
      q: "Do I need to book each car independently?",
      a: "No. Add all shortlisted cars—any mix of Normal/Luxury and types—to the cart and check out once to reserve them all.",
    },
  ];

  return (
    <section className="py-16 bg-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <h2 className="text-3xl font-bold">Frequently asked questions</h2>
          <p className="mt-2 text-slate-600">
            Need more help?{" "}
            <Link
              to="/contact"
              className="text-slate-900 underline decoration-slate-300 hover:decoration-slate-900"
            >
              Contact us
            </Link>
            .
          </p>
        </div>

        <div className="mt-8 divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white">
          {items.map((it) => (
            <div key={it.q} className="p-6">
              <div className="flex items-start gap-3">
                <span
                  className="mt-1 grid h-6 w-6 place-items-center rounded-full text-white text-xs font-bold"
                  style={{ background: BRAND.pink }}
                >
                  ?
                </span>
                <div>
                  <h3 className="text-base font-semibold">{it.q}</h3>
                  <p className="mt-1 text-sm text-slate-600">{it.a}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =========================
   Final CTA
   ========================= */
function FinalCTA() {
  return (
    <section className="py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div
          className="rounded-3xl p-10 text-white shadow-[0_24px_80px_-30px_rgba(2,6,23,0.35)]"
          style={{
            background: `linear-gradient(135deg, ${BRAND.black}, ${BRAND.pink})`,
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
            <div className="md:col-span-2">
              <h3 className="text-2xl font-bold">
                Ready to plan smarter logistics?
              </h3>
              <p className="mt-1 text-white/90">
                Create one plan, reserve many cars, manage everything online.
              </p>
            </div>
            <div className="flex md:justify-end gap-3">
              <Link
                to="/discover"
                className="inline-flex items-center rounded-2xl bg-white px-5 py-3 text-base font-semibold text-slate-900 shadow"
              >
                Start a booking
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* =========================
   Footer
   ========================= */
function Footer() {
  return (
    <footer className="border-t border-slate-200 mt-8">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 md:grid-cols-3 gap-10">
        <div>
          <img
            src="/wayrootLogo.svg"
            alt="Wayroot"
            className="h-9 w-auto md:h-10"
          />
          <p className="mt-4 text-sm text-slate-600 max-w-sm">
            Wayroot simplifies multi-car reservations for events, corporates,
            and group travel — with live availability, true time-based
            reservations, and transparent pricing.
          </p>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-slate-900">Product</h4>
          <ul className="mt-3 space-y-2 text-sm text-slate-600">
            <li>
              <Link to="/discover" className="hover:text-slate-900">
                Schedule Trip
              </Link>
            </li>
            <li>
              <Link to="/my-bookings" className="hover:text-slate-900">
                My Bookings
              </Link>
            </li>
            <li>
              <Link to="/cart" className="hover:text-slate-900">
                Cart
              </Link>
            </li>
            <li>
              <Link to="/pricing" className="hover:text-slate-900">
                Pricing
              </Link>
            </li>
          </ul>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-slate-900">Company</h4>
          <ul className="mt-3 space-y-2 text-sm text-slate-600">
            <li>
              <Link to="/about-wayroot" className="hover:text-slate-900">
                About
              </Link>
            </li>
            <li>
              <Link to="/careers" className="hover:text-slate-900">
                Careers
              </Link>
            </li>
            <li>
              <Link to="/contact" className="hover:text-slate-900">
                Contact</Link>
            </li>
            <li>
              <Link to="/security" className="hover:text-slate-900">
                Security
              </Link>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-slate-200 py-6 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} Wayroot. All rights reserved.
      </div>
    </footer>
  );
}

/* =========================
   Atoms & Icons
   ========================= */
function Badge({ children, icon }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-3.5 py-2 shadow-sm backdrop-blur-sm">
      <span className="grid h-5 w-5 place-items-center rounded-full bg-slate-50 ring-1 ring-slate-200 text-slate-700">
        {icon}
      </span>
      <span className="text-slate-700">{children}</span>
    </span>
  );
}

function GradientIcon() {
  return (
    <span
      className="inline-grid place-items-center rounded-xl p-[2px]"
      style={{ background: GRADIENT }}
    >
      <span className="h-9 w-9 rounded-[10px] bg-white grid place-items-center">
        <SparkIcon />
      </span>
    </span>
  );
}

/* small SVG icons reused from your Home file */
function SparkIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-4 w-4 text-slate-900"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <path d="M12 2l3 6 6 3-6 3-3 6-3-6-6-3 6-3z" />
    </svg>
  );
}
function ShieldIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-3.5 w-3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <path d="M12 2l7 4v6a9 9 0 0 1-7 8 9 9 0 0 1-7-8V6l7-4z" />
    </svg>
  );
}
function LockIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-3.5 w-3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <rect x="4" y="10" width="16" height="10" rx="2" />
      <path d="M8 10V7a4 4 0 1 1 8 0v3" />
    </svg>
  );
}
function BoltIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-3.5 w-3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <path d="M13 2L3 14h7l-1 8 11-12h-7l1-8z" />
    </svg>
  );
}
